import { useQuery } from "@tanstack/react-query"
import { Palette } from "lucide-react"
import { Link } from "react-router"

import { api } from "@/api/cliente"
import type { Empresa } from "@/componentes/empresa/tipos"
import { CODIGOS_ARCHIVO } from "@/componentes/identidad/identidad"
import { MarcaCompania } from "@/componentes/impresos/MarcaCompania"
import { Ondas } from "@/componentes/impresos/Ondas"
import type { DatosRotulo } from "@/componentes/rotulacion/tipos"
import { VistaRotulo } from "@/componentes/rotulacion/VistaRotulo"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * El manual de identidad documental: Imagen Corporativa → «Manual de identidad».
 *
 * ⚠ **Las piezas de abajo son los componentes REALES de la rotulación**, no dibujos: si
 * cambia un rótulo, cambia aquí. Un manual con ejemplos aparte acabaría contradiciendo
 * lo que sale de la impresora.
 *
 * ⚠ **Va sobre `.pieza-impresa`, papel blanco en cualquier tema**: enseña tinta sobre
 * papel, con sus colores de verdad, y el área de trabajo los cambiaría.
 */

const COLORES = [
  {
    nombre: "Vino noche",
    color: "var(--impreso-noche)",
    uso: "#1A0A12 · color de la web. Títulos y texto principal.",
  },
  {
    nombre: "Borgoña",
    color: "var(--impreso-vino)",
    uso: "#7A1B38 · logo, cinta, códigos y etiquetas.",
  },
  {
    nombre: "Oro",
    color: "var(--impreso-oro-2)",
    uso: "#C99A43 · solo filetes y líneas, nunca texto.",
  },
  {
    nombre: "Papel",
    color: "var(--impreso-papel)",
    uso: "Blanco o cartulina de color. Ocupa más del 85 % de cada pieza.",
  },
]

const REGLAS = [
  "Orden de lectura fijo: código, departamento, contenido y período. El ojo siempre encuentra lo mismo en el mismo lugar.",
  "El logo va en borgoña sobre blanco y siempre separado de la cinta, con un margen igual a su altura de letra.",
  "El oro solo traza filetes de 1 px. El texto va en vino noche o borgoña, nunca en dorado.",
  "Los márgenes son generosos (19 mm en carátulas, 4 mm en etiquetas) y no se usan recuadros ni sombras.",
]

/** Los ejemplos del manual. Son de muestra: no salen de ningún dato del sistema. */
const PIEZAS: { nombre: string; escala: number; datos: DatosRotulo }[] = [
  {
    nombre: "Carátula · carta 216 × 279 mm",
    escala: 0.45,
    datos: {
      tipo: "CARATULA",
      compania: "SETIECA_AGENTES",
      hoja: "CARTA",
      codigo: "FI–012",
      departamento: "Finanzas",
      titulo: "Declaraciones de IVA",
      subtitulo: null,
      entidad: null,
      rif: null,
      periodo: "Enero – junio 2026",
      seccion: "Tributos",
      responsable: "[Nombre]",
      ubicacion: "Archivo 2 · Caja 04",
      con_membrete: true,
    },
  },
  {
    nombre: "Lomo · 60 × 180 mm",
    escala: 0.7,
    datos: {
      tipo: "LOMO",
      compania: "SETIECA_AGENTES",
      titulo: "Declaraciones de IVA",
      subtitulo: "Finanzas",
      siglas: "FI–012",
      contenido: null,
      fecha: "2026",
      ancho_mm: 60,
      alto_mm: 180,
    },
  },
  {
    nombre: "Etiqueta de tapa · 190 × 128 mm",
    escala: 0.6,
    datos: {
      tipo: "ETIQUETA_CARPETA",
      compania: "SETIECA_AGENTES",
      empresa: "",
      siglas: "FI–012",
      nombre_expediente: "Declaraciones de IVA",
      seccion_archivo: "Finanzas",
      periodo: "2026",
      secciones: [
        { descripcion: "Declaraciones presentadas" },
        { descripcion: "Libros de compras y ventas" },
        { descripcion: "Retenciones" },
        { descripcion: "Soportes de pago" },
      ],
    },
  },
  {
    nombre: "Tejuelo · 148 × 105 mm",
    escala: 0.6,
    datos: {
      tipo: "TEJUELO",
      compania: "SETIECA_AGENTES",
      codigo: "Libro N.º 3 · AD–001",
      libro: "Libro de actas",
      detalle: "Junta de socios · 2024 – 2026",
      empresa: "",
      rif: null,
    },
  },
  {
    nombre: "Pestaña · 90 × 16 mm",
    escala: 1,
    datos: {
      tipo: "PESTANA",
      compania: "SETIECA_AGENTES",
      departamento: "Finanzas",
      contenido: "Declaraciones de IVA",
      siglas: "FI–012",
      periodo: "2026",
    },
  },
]

const SECCION = { fontFamily: "var(--font-rotulo)", color: "var(--impreso-vino)" }
const SUAVE = { color: "var(--impreso-texto-2)" }
const LINEA = { borderColor: "var(--impreso-filete)" }

function Titulo({ children }: { children: string }) {
  return (
    <h2 className="text-xs font-bold uppercase tracking-[0.24em]" style={SECCION}>
      {children}
    </h2>
  )
}

export function ManualIdentidad() {
  const usuario = usarSesion((e) => e.usuario)
  const empresa = useQuery({
    queryKey: ["empresa"],
    queryFn: () => api.get<Empresa>("/api/v1/empresa"),
  })

  return (
    <div>
      <header className="no-print mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Palette size={20} aria-hidden={true} />
            Manual de identidad
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Las reglas de todo lo que se rotula en la agencia.
          </p>
        </div>
        {tienePermiso(usuario, "rotulo:ver") && (
          <Link to="/rotulacion" className="boton-secundario h-9 px-3 text-sm">
            Ir a Rotulación
          </Link>
        )}
      </header>

      <div
        className="pieza-impresa relative grid gap-10 p-5 sm:p-8 lg:p-12"
        style={{ width: "100%", fontFamily: "var(--font-rotulo-texto)" }}
      >
        <header
          className="flex flex-col gap-6 border-b pb-8 sm:flex-row sm:items-center"
          style={LINEA}
        >
          <MarcaCompania alto={28} />
          <div
            className="hidden w-px self-stretch sm:block"
            style={{ background: "var(--impreso-oro-2)" }}
          />
          <div className="grid gap-2">
            <p className="text-xs font-bold uppercase tracking-[0.24em]" style={SECCION}>
              Sistema de rotulación
            </p>
            <p
              className="text-3xl font-extrabold tracking-tight sm:text-5xl"
              style={{ fontFamily: "var(--font-rotulo)", color: "var(--impreso-noche)" }}
            >
              Identidad documental
            </p>
            <p className="max-w-xl text-base" style={SUAVE}>
              Setieca Agentes Aduanales, C.A. · Puerto Cabello. Una sola voz para carátulas, lomos,
              pestañas, tapas y tejuelos.
            </p>
            <p className="tabular text-xs" style={SUAVE}>
              Versión 1.0 · Octubre 2026
            </p>
          </div>
        </header>

        <div className="grid gap-10 lg:grid-cols-3">
          <section className="grid content-start gap-4">
            <Titulo>01 · Color</Titulo>
            <div className="grid grid-cols-2 gap-3">
              {COLORES.map((c) => (
                <div key={c.nombre} className="grid content-start gap-2">
                  <div className="h-20 rounded border" style={{ background: c.color, ...LINEA }} />
                  <strong className="text-sm" style={{ fontFamily: "var(--font-rotulo)" }}>
                    {c.nombre}
                  </strong>
                  <span className="text-xs" style={SUAVE}>
                    {c.uso}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="grid content-start gap-4">
            <Titulo>02 · Tipografía</Titulo>
            <div className="grid gap-1 border-b pb-4" style={LINEA}>
              <span
                className="text-4xl font-extrabold tracking-tight"
                style={{ fontFamily: "var(--font-rotulo)" }}
              >
                Finanzas
              </span>
              <span className="text-xs" style={SUAVE}>
                Plus Jakarta Sans ExtraBold · títulos y códigos
              </span>
            </div>
            <div className="grid gap-1 border-b pb-4" style={LINEA}>
              <span className="text-sm font-bold uppercase tracking-[0.24em]" style={SECCION}>
                Talento humano
              </span>
              <span className="text-xs" style={SUAVE}>
                Plus Jakarta Sans Bold, versales espaciadas 0,24 em · departamento
              </span>
            </div>
            <div className="grid gap-1">
              <span className="text-lg font-medium">Declaraciones de IVA, enero – junio 2026</span>
              <span className="text-xs" style={SUAVE}>
                Inter Medium · contenido, períodos y datos
              </span>
            </div>
          </section>

          <section className="grid content-start gap-4">
            <Titulo>03 · Ondas translúcidas</Titulo>
            <div className="relative h-36 overflow-hidden rounded border" style={LINEA}>
              <Ondas pieza="muestra" style={{ inset: 0, width: "100%", height: "100%" }} />
            </div>
            <p className="text-sm" style={{ color: "var(--impreso-texto-3)" }}>
              Dos o tres velos de borgoña por pieza que se cruzan como olas en el puerto. Cada capa
              es semitransparente (del 30 al 55 %), así el cruce crea un tono más profundo de la
              misma familia. Entran siempre por un borde y dejan libre la zona de lectura.
            </p>
          </section>
        </div>

        <div className="grid gap-10 lg:grid-cols-2">
          <section className="grid content-start gap-4">
            <Titulo>04 · Codificación de archivo</Titulo>
            <div className="flex flex-wrap items-baseline gap-4">
              <span
                className="tabular text-4xl font-extrabold"
                style={{ fontFamily: "var(--font-rotulo)" }}
              >
                FI–012
              </span>
              <span className="text-sm" style={SUAVE}>
                Departamento + número de expediente, con raya y tres dígitos.
              </span>
            </div>
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Códigos de archivo por departamento</caption>
              <thead>
                <tr>
                  {["Código", "Departamento"].map((t) => (
                    <th
                      key={t}
                      scope="col"
                      className="border-b py-2 text-left text-xs uppercase tracking-[0.18em]"
                      style={{ borderColor: "var(--impreso-noche)" }}
                    >
                      {t}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CODIGOS_ARCHIVO.map((fila) => (
                  <tr key={fila.codigo}>
                    <td
                      className="border-b py-2 font-semibold"
                      style={{ ...LINEA, color: "var(--impreso-vino)" }}
                    >
                      {fila.codigo}
                    </td>
                    <td className="border-b py-2" style={LINEA}>
                      {fila.departamento}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="grid content-start gap-4">
            <Titulo>05 · Reglas de composición</Titulo>
            <ol className="grid gap-3 text-sm" style={{ color: "var(--impreso-texto-3)" }}>
              {REGLAS.map((regla, i) => (
                <li key={regla} className="flex gap-4">
                  <span
                    className="font-extrabold"
                    style={{ fontFamily: "var(--font-rotulo)", color: "var(--impreso-vino)" }}
                  >
                    {i + 1}
                  </span>
                  <span>{regla}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>

        <section className="grid gap-4">
          <Titulo>06 · Las piezas</Titulo>
          <div className="flex flex-wrap items-end gap-6">
            {PIEZAS.map((pieza) => (
              <figure key={pieza.nombre} className="m-0 grid max-w-full gap-2 overflow-x-auto">
                <div className="w-fit border" style={{ ...LINEA, zoom: pieza.escala }}>
                  <VistaRotulo datos={pieza.datos} empresa={empresa.data} />
                </div>
                <figcaption className="text-xs" style={SUAVE}>
                  {pieza.nombre}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}
